import React, { useMemo } from "react";
import {
  FormRadioGroup,
  FormTextarea,
  FormFileUpload
} from "protocol-setup/components/FormComponents";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import makeFileUploadRequest from "protocol-setup/redux/makeFileUploadRequest";
import {
  defaultErrorMessage,
  ctsuTestingUrl
} from "protocol-setup/redux/constants";
import {
  OpenErrorNotification,
  OpenSuccessNotification
} from "protocol-setup/components/notification";
import ButtonGroupSignoff from "protocol-setup/components/FormComponents/ButtonGroupSignoff";
import { dispatchRefreshEvent, useSetupPermission } from "protocol-setup/helpers";
import errorMessages, { supportedFileTypes, COMMENTS_MAX_LENGTH } from "protocol-setup/constants/errorMessages";
import { useRequestHistorySubmit } from "protocol-setup/helpers/withRequestHistory";
import ProtectionElements from 'protocol-setup/helpers/ProtectionElement';
import { calculateFilesToDelete, handleFileDeletions } from "protocol-setup/helpers/fileHelpers";
import { tabNames } from "../orHelpers";

import { yesNoOptions } from "../Lovs";

export const handleSubmitForm = async (
  data,
  requestNumber,
  onSuccessCallback,
  initialFiles = []
) => {
  try {
    const fd = new FormData();
    fd.append(
      "reqStr",
      JSON.stringify({
        approvalStatus: data.isCTSUCompletedTesting,
        comment: data.comments,
        requestNumber
      })
    );

    // Handle file uploads
    if (Array.isArray(data.files) && data.files.length > 0) {
      data.files.forEach((file) => {
        if (file instanceof Blob || file instanceof File) {
          const { name } = file;
          fd.append("files", file, name);
        }
      });
    }

    await makeFileUploadRequest(ctsuTestingUrl, fd, "POST");

    const filesToDelete = calculateFilesToDelete(data.files, initialFiles);
    await handleFileDeletions(filesToDelete);

    if (onSuccessCallback) {
      await onSuccessCallback();
    }

    OpenSuccessNotification({
      message: "CTSU Testing form successfully submitted.",
      autoHide: true
    });
  } catch (e) {
    OpenErrorNotification({
      message: defaultErrorMessage,
      autoHide: true
    });
  }
};

export const CTSUTestingSchema = yup.object().shape({

  isCTSUCompletedTesting: yup.boolean().nullable().required("Required").test(
    'not-null',
    'Required',
    (value) => value !== null
  ),
  comments: yup
    .string()
    .max(COMMENTS_MAX_LENGTH, errorMessages.openRave.ctsuTestingForm.comments.max)
    .when("isCTSUCompletedTesting", ([isCTSUCompletedTesting], schema) => {
      return isCTSUCompletedTesting === false
        ? schema.required(errorMessages.openRave.ctsuTestingForm.comments.required)
        : schema.optional();
    }),
  files: yup
    .array()
    .test(
      "fileFormat",
      errorMessages.common.fileUpload.allowedFormats,
      (files) => {
        if (!files || files.length === 0) return true;
        const supportedFormats = supportedFileTypes
        return files.every(
          (file) =>
            !file ||
            supportedFormats.includes(file.name?.split(".").pop().toLowerCase())
        );
      }
    )
    .optional()
});

const convertDataFormat = (formData) => {
  return formData.map((f) => {
    return (
      {
        isCTSUCompletedTesting: f?.IS_GROUP_TESTING_COMPLETED_YN === "Y" || null,
        comments: f?.COMMENTS || null,
        files:
          f.uploaded_docs?.map((x) => {
            return { name: x.DOC_FILE_NAME, id: x.DOCUMENT_ID };
          }) || [],
        submitDate: f.SUBMITTED_DT,
        submitBy: f.SUBMITTED_BY_NAME
      }
    )
  })
};

export const getFiles = (formData) => {
  const files =[];
  if (formData) {
    formData.forEach((f) => {
      files.push(...(f.files ?? []));
    })
  }
  return files
}

const CTSUTestingForm = ({ protocolSetupData, fetchPsData, readOnly }) => {
  const { requestNumber, ctsuTestDtoList } = protocolSetupData || {};

  const ctsuTestingData = useMemo(() => {
    try {
      const userTypeCommentsJson = ctsuTestDtoList?.[0]?.userTypeCommentsJson;
      if (!userTypeCommentsJson) return [];
      return convertDataFormat(JSON.parse(userTypeCommentsJson));
    } catch {
      return [];
    }
  }, [ctsuTestDtoList]);

  
  const {
    control,
    handleSubmit,
    reset,
    watch,
    trigger,
    formState: { dirtyFields }
  } = useForm({
    resolver: yupResolver(CTSUTestingSchema),
    defaultValues: {
      isCTSUCompletedTesting: ctsuTestingData?.[0]?.isCTSUCompletedTesting ?? null,
      comments:  "", // comments will be populated from commentsUnderLabel prop
      files: getFiles(ctsuTestingData)
    }
  });

  const hasSignOffPermission = useSetupPermission(ProtectionElements.OPEN_SIGNOFF_CTSUTESTING);
  const isSignOffEditable = !readOnly && hasSignOffPermission;

  const onSuccess = async () => {
    await fetchPsData();
    dispatchRefreshEvent();
  };

  // Wrap handleSubmitForm with request history tracking
  const onSubmitWithHistory = useRequestHistorySubmit(
    (data, onSuccessCallback) =>
      handleSubmitForm(data, requestNumber, onSuccessCallback, getFiles(ctsuTestingData)),
    dirtyFields,
    tabNames.SIGNOFF_CTSU_TESTING,
    "Signoff"
  );

  const onSubmit = (data) => {
    if (readOnly) return;
    const d = { ...data, requestNumber };
    onSubmitWithHistory(d, onSuccess);
  };

  const onReset = () => {
    reset();
  };

  return (
    <div
      className={`form-layout-wrapper bg-transparent ${
        readOnly ? "read-only" : ""
      }`}
    >
      <form
        id="CTSUTestingForm"
        className="bg-transparent"
        onSubmit={handleSubmit(onSubmit)}
        encType="multipart/form-data"
      >
        <div className="form-layout-body px-0">
          <span className="d-none"></span>
          <ul className="row">
            <ul className="col-8 mr-4 mb-0">
              <li className="d-block">
                <FormRadioGroup
                  name="isCTSUCompletedTesting"
                  section="openRave"
                  form="ctsuTestingForm"
                  control={control}
                  options={yesNoOptions}
                  required
                  disabled={!isSignOffEditable}
                  trigger={trigger}
                  wrapperClassName="mb-3"
                />
                <FormTextarea
                  name="comments"
                  section="openRave"
                  form="ctsuTestingForm"
                  control={control}
                  rows={3}
                  disabled={!isSignOffEditable}
                  required={watch("isCTSUCompletedTesting") === false}
                  commentsUnderLabel={ctsuTestingData}
                  data-testid="form-textarea-comments"
                  trigger={trigger}
                  wrapperClassName="mb-3"
                />
                <FormFileUpload
                  name="files"
                  section="openRave"
                  form="ctsuTestingForm"
                  control={control}
                  accept=".doc,.docx,.pdf"
                  multiple
                  disabled={!isSignOffEditable}
                />
              </li>
            </ul>
          </ul>
        </div>
        <div className="form-layout-footer px-0 pb-0">
          {isSignOffEditable && (
            <ButtonGroupSignoff
              onSubmit={onSubmit}
              onReset={onReset}
              control={control}
              protectionElement= {ProtectionElements.OPEN_SIGNOFF_CTSUTESTING}
            />
          )}
        </div>
      </form>
    </div>
  );
};

export default CTSUTestingForm;
